import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import * as Sentry from '@sentry/react-native';
import { usePostHog } from 'posthog-js/react';
import { REDESIGN_COLORS } from '../../styles/colors';
import { HIDDEN_SIGNALS, SIGNAL_FRIENDLY_LABELS } from '../../config/listingsConfig';
import { useTier } from '../../context/TierContext';
import { getSignalTag } from '../../utils/scoreTags';
import { ProInlineUnlock } from '../common/ProLock';
import CollapsibleSection from './CollapsibleSection';
import SignalTagBadge from './SignalTagBadge';

const CautionIcon = ({ size = 16 }) => (
  <Svg width={size} height={size * (111.54 / 122.88)} viewBox="0 0 122.88 111.54">
    <Path d="M2.35,84.42,45.28,10.2l.17-.27h0A23,23,0,0,1,52.5,2.69,17,17,0,0,1,61.57,0a16.7,16.7,0,0,1,9.11,2.69,22.79,22.79,0,0,1,7,7.26q.19.32.36.63l42.23,73.34.24.44h0a22.48,22.48,0,0,1,2.37,10.19,17.63,17.63,0,0,1-2.17,8.35,15.94,15.94,0,0,1-6.93,6.6c-.19.1-.39.18-.58.26a21.19,21.19,0,0,1-9.11,1.75v0H17.61c-.22,0-.44,0-.65,0a18.07,18.07,0,0,1-6.2-1.15A16.42,16.42,0,0,1,3,104.24a17.53,17.53,0,0,1-3-9.57,23,23,0,0,1,1.57-8.74,7.66,7.66,0,0,1,.77-1.51Z" fill="#1a1a1a" />
    <Path d="M9,88.75,52.12,14.16c5.24-8.25,13.54-8.46,18.87,0l42.43,73.69c3.39,6.81,1.71,16-9.33,15.77H17.61C10.35,103.8,5.67,97.43,9,88.75Z" fill="#ffef11" fillRule="evenodd" />
    <Path d="M57.57,82.69A5.48,5.48,0,0,1,61,81.11a5.66,5.66,0,0,1,4.42,1.66,5.53,5.53,0,0,1,1.62,3.54A5.6,5.6,0,0,1,67,87.72a6,6,0,0,1-.42,1.34,5.53,5.53,0,0,1-2.92,2.84,5.79,5.79,0,0,1-4.57-.1,5.51,5.51,0,0,1-2.81-2.94,5.31,5.31,0,0,1-.36-1.27,5.9,5.9,0,0,1-.06-1.32h0a6.45,6.45,0,0,1,.57-2,4.6,4.6,0,0,1,1.14-1.56Zm8.15-10.24c-.19,4.79-8.31,4.8-8.49,0-.82-8.21-2.92-27.7-2.86-35.42.07-2.38,2-3.78,4.56-4.32a11.19,11.19,0,0,1,2.49-.24,11.77,11.77,0,0,1,2.5.25c2.61.55,4.65,2,4.65,4.44v.24L65.72,72.45Z" fill="#1a1a1a" />
  </Svg>
);

const MileageWarningBadge = ({ motMiles }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const formatted = motMiles != null ? Number(motMiles).toLocaleString('en-GB') : '';
  return (
    <View
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={warningStyles.wrapper}
    >
      <CautionIcon size={15} />
      {showTooltip && (
        <View style={warningStyles.tooltip}>
          <View style={warningStyles.tooltipArrow} />
          <Text style={warningStyles.tooltipText}>
            The advertised mileage is lower than the last MOT reading of {formatted} mi.
            This may indicate an error in the listing or a potential odometer discrepancy.
          </Text>
        </View>
      )}
    </View>
  );
};

const warningStyles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    cursor: 'pointer',
    marginLeft: 6,
    alignSelf: 'center',
  },
  tooltip: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: [{ translateX: '-50%' }],
    marginBottom: 8,
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    width: 240,
    boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
    zIndex: 100,
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -5,
    left: '50%',
    marginLeft: -5,
    width: 10,
    height: 10,
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    transform: [{ rotate: '45deg' }],
  },
  tooltipText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: '#F59E0B',
    lineHeight: 17,
  },
});

/**
 * Detect unusual mileage accumulation rate changes from MOT history.
 * Returns null if no anomaly, or { rateA, rateB, ratio, direction } for worst hit.
 */
const detectMileageRateAnomaly = (motHistory) => {
  if (!motHistory) return null;
  const motTests = Array.isArray(motHistory.mot_tests) ? motHistory.mot_tests : [];
  if (motTests.length < 2) return null;

  // Build chronological points, deduplicated by calendar date (keep max odometer)
  const byDate = {};
  for (let i = motTests.length - 1; i >= 0; i--) {
    const t = motTests[i];
    const val = parseInt(t.odometerValue, 10);
    if (!val || val <= 0) continue;
    const d = new Date(t.completedDate);
    if (isNaN(d.getTime())) continue;
    const key = d.toISOString().slice(0, 10);
    if (!byDate[key] || val > byDate[key].miles) {
      byDate[key] = { date: d, miles: val };
    }
  }

  const points = Object.values(byDate).sort((a, b) => a.date - b.date);

  // Prepend registration date as point zero (0 miles)
  if (motHistory.first_used_date) {
    const reg = new Date(motHistory.first_used_date + 'T00:00:00');
    if (!isNaN(reg.getTime()) && points.length > 0 && reg < points[0].date) {
      points.unshift({ date: reg, miles: 0 });
    }
  }

  if (points.length < 3) return null;

  // Calculate annualised rates, skip intervals < 3 months
  const rates = [];
  for (let i = 0; i < points.length - 1; i++) {
    const years = (points[i + 1].date - points[i].date) / (365.25 * 86400000);
    if (years < 0.25) continue;
    const gained = points[i + 1].miles - points[i].miles;
    rates.push(gained / years);
  }

  if (rates.length < 2) return null;

  // Find worst adjacent rate ratio
  let worst = null;
  for (let i = 0; i < rates.length - 1; i++) {
    const lo = Math.min(rates[i], rates[i + 1]);
    const hi = Math.max(rates[i], rates[i + 1]);
    if (lo < 100) continue; // skip near-zero denominators
    const ratio = hi / lo;
    const absDiff = hi - lo;
    if (ratio > 3.0 && absDiff > 5000) {
      if (!worst || ratio > worst.ratio) {
        worst = {
          rateA: Math.round(rates[i]),
          rateB: Math.round(rates[i + 1]),
          ratio: Math.round(ratio * 10) / 10,
          direction: rates[i] > rates[i + 1] ? 'HIGH_TO_LOW' : 'LOW_TO_HIGH',
        };
      }
    }
  }

  return worst;
};

const RateAnomalyBadge = ({ anomaly }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  if (!anomaly) return null;

  const fmtRate = (r) => Number(r).toLocaleString('en-GB');
  const isDropping = anomaly.direction === 'HIGH_TO_LOW';

  return (
    <View
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={warningStyles.wrapper}
    >
      <CautionIcon size={15} />
      {showTooltip && (
        <View style={warningStyles.tooltip}>
          <View style={warningStyles.tooltipArrow} />
          <Text style={warningStyles.tooltipText}>
            Unusual mileage pattern detected in MOT history: rate {isDropping ? 'dropped' : 'jumped'} from ~{fmtRate(anomaly.rateA)} mi/yr
            to ~{fmtRate(anomaly.rateB)} mi/yr ({anomaly.ratio}x change).
            {isDropping
              ? ' A sharp drop in recorded mileage may indicate odometer tampering.'
              : ' A sharp increase after low usage may indicate a change in ownership or usage pattern.'}
          </Text>
        </View>
      )}
    </View>
  );
};

/**
 * Display templates for score breakdown signals.
 * Each returns { primary, secondary } text for a given signal + context.
 */
const SIGNAL_DISPLAY = {
  price_vs_market: (signal) => {
    // Use the scoring engine's explanation — it's the authoritative source
    // computed at feed generation time against the correct market segment
    return { primary: signal.explanation || 'Price vs Market', secondary: 'Price vs Market' };
  },
  distance: (signal, snapshot) => ({
    primary: snapshot?.distance_miles != null
      ? `${Math.round(snapshot.distance_miles)} miles away`
      : signal.explanation || 'Distance',
    secondary: 'Distance to seller',
  }),
  mileage: (signal, snapshot, marketAverages, motHistory) => {
    const miles = snapshot?.miles;
    const avgMiles = marketAverages?.medianMiles;
    const primary = miles != null ? `${Number(miles).toLocaleString('en-GB')} miles` : signal.explanation || 'Mileage';

    let secondary = 'Mileage';
    if (miles != null && avgMiles != null && avgMiles > 0) {
      const diff = Math.abs(Math.round(miles - avgMiles));
      const diffFormatted = diff.toLocaleString('en-GB');
      if (miles < avgMiles) {
        secondary = `${diffFormatted} below average for this model`;
      } else if (miles > avgMiles) {
        secondary = `${diffFormatted} above average for this model`;
      } else {
        secondary = 'Average mileage for this model';
      }
    }

    const motMiles = motHistory?.latest_odometer_value != null ? Number(motHistory.latest_odometer_value) : null;
    const mileageWarning = miles != null && motMiles != null && (motMiles - Number(miles)) >= 500 ? motMiles : null;
    const rateAnomaly = detectMileageRateAnomaly(motHistory);

    return { primary, secondary, mileageWarning, rateAnomaly };
  },
  dom: (signal) => {
    const exp = signal.explanation || '';
    const match = exp.match(/(\d+)\s*day/i);
    const days = match ? match[1] : '—';

    let secondary = 'Days on market';
    if (exp.startsWith('New listing')) {
      secondary = 'Recently listed';
    } else if (exp.includes('typical for this model')) {
      secondary = 'Typical for this model';
    } else if (exp.includes('likely negotiable')) {
      secondary = 'Seller may accept lower offers';
    } else if (exp.includes('may be negotiable')) {
      secondary = 'Seller may accept lower offers';
    }

    return { primary: `Listed ${days} days ago`, secondary };
  },
  year_match: (signal, snapshot) => ({
    primary: snapshot?.year != null ? `${snapshot.year}` : signal.explanation || 'Year',
    secondary: 'Within the recommended year range',
  }),
  price_trend: (signal, snapshot, _marketAverages, _motHistory, priceChange) => {
    // Prefer the detected drop tracked on the feed entry (Step 21) over the
    // snapshot's ref_price, which lags behind dealer-site rescrapes.
    const current = priceChange?.current_price ?? snapshot?.price;
    const previous = priceChange?.previous_price ?? snapshot?.previous_price ?? snapshot?.ref_price;
    if (current != null && previous != null && previous > current) {
      const drop = priceChange?.price_change_amount != null && priceChange.price_change_amount < 0
        ? Math.abs(priceChange.price_change_amount)
        : Math.round(previous - current);
      return {
        primary: `£${Number(drop).toLocaleString('en-GB')} dropped`,
        secondary: `Initial price was £${Number(previous).toLocaleString('en-GB')}`,
      };
    }
    return {
      primary: signal.explanation || 'Price trend',
      secondary: 'Price trend',
    };
  },
  fuel_match: (signal, snapshot) => ({
    // Left = the data (the actual fuel type). Right tag carries the verdict
    // ("Fuel match" / "Different fuel"); avoid echoing the same word.
    primary: snapshot?.fuel_type || signal.explanation || 'Fuel type',
    secondary: 'Matches your fuel preference',
  }),
  owners: (signal, snapshot) => {
    const count = snapshot?.num_owners;
    if (count != null && count > 0) {
      // V5C terminology — match the right-column tag wording ("keepers", not "owners").
      return {
        primary: count === 1 ? '1 previous keeper' : `${count} previous keepers`,
        secondary: 'Previous ownership history',
      };
    }
    return { primary: signal.explanation || 'Keepers', secondary: 'Ownership history' };
  },
  trim_match: (signal, snapshot) => {
    // Left = the listing's actual trim/spec from the snapshot, not an
    // evaluative ladder ("Great trim" / "Well equipped") which contradicts the
    // right-column tag when the score is mid or low.
    const trim = snapshot?.trim ? String(snapshot.trim).trim() : '';
    return {
      primary: trim || signal.explanation || 'Trim level',
      secondary: 'Trim level',
    };
  },
  transmission_match: (signal) => ({
    primary: signal.explanation || 'Transmission',
    secondary: 'Transmission type',
  }),
  ulez: (signal) => ({
    primary: signal.explanation || 'ULEZ',
    secondary: 'Low emission zone compliance',
  }),
};

/** Order in which signals should be displayed. */
const DISPLAY_ORDER = [
  'price_vs_market',
  'distance',
  'mileage',
  'trim_match',
  'dom',
  'year_match',
  'price_trend',
  'transmission_match',
  'fuel_match',
  'owners',
  'ulez',
];

/**
 * ScoreBreakdownSection - Collapsible section showing per-criterion score rows.
 *
 * @param {Object} props
 * @param {Array} props.scoreBreakdown - score_breakdown array from feed entry
 * @param {Object} props.listingSnapshot - listing_snapshot from feed entry
 * @param {Object} props.marketAverages - market_averages from detail endpoint
 */
const ScoreBreakdownSection = ({ scoreBreakdown, listingSnapshot, marketAverages, motHistory, priceChange, onToggle, hideTopBorder = false, modelId, marketcheckId, forceUnlocked = false, onRowsMeta = null, highlightRowKey = null, highlightRowRef = null, rowHighlightStyle = null }) => {
  const { hasProAccess, triggerUpgradeModal, trialUnlockEligible } = useTier();
  const posthog = usePostHog();
  // forceUnlocked: the first-session tour previews the full breakdown un-blurred
  // even for Free users (option c), so step 1 never lands on a Pro lock.
  const effectivePro = hasProAccess || forceUnlocked;

  const rows = useMemo(() => {
    if (!scoreBreakdown || !Array.isArray(scoreBreakdown)) {
      return [];
    }

    try {
      // Build lookup by signal key
      const signalMap = {};
      for (const item of scoreBreakdown) {
        if (item?.signal) {
          signalMap[item.signal] = item;
        }
      }

      const result = [];
      for (const key of DISPLAY_ORDER) {
        // Skip hidden signals
        if (HIDDEN_SIGNALS.has(key)) continue;

        const signal = signalMap[key];
        if (!signal) continue;

        // Hide price_trend when no reference point exists (neither ulf-level
        // detection nor snapshot ref_price).
        if (key === 'price_trend') {
          const refPrice = priceChange?.previous_price
            ?? listingSnapshot?.ref_price
            ?? listingSnapshot?.previous_price;
          if (refPrice == null) continue;
        }

        // Hide owners when num_owners is 0 or unknown
        if (key === 'owners') {
          const count = Number(listingSnapshot?.num_owners);
          if (!count || count <= 0) continue;
        }

        const displayFn = SIGNAL_DISPLAY[key];
        const display = displayFn
          ? displayFn(signal, listingSnapshot, marketAverages, motHistory, priceChange)
          : { primary: signal.explanation || key, secondary: SIGNAL_FRIENDLY_LABELS[key] || key };

        // Resolve tag for the right column. When the resolver returns null
        // (e.g. price_trend "Stable"), suppress the row entirely.
        const tag = getSignalTag(key, signal.value, signal.explanation, listingSnapshot);
        if (tag === null) continue;

        result.push({
          key,
          score: signal.value,
          tag,
          primary: display.primary,
          secondary: display.secondary,
          mileageWarning: display.mileageWarning || null,
          rateAnomaly: display.rateAnomaly || null,
        });
      }

      return result;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: 'ScoreBreakdownSection' },
        level: 'warning',
      });
      return [];
    }
  }, [scoreBreakdown, listingSnapshot, marketAverages, motHistory]);

  // Report which factor rows rendered + whether mileage is flagged, so the
  // first-session tour can add row stops only for factors this car actually has.
  const rowsMetaSigRef = useRef('');
  useEffect(() => {
    if (!onRowsMeta) return;
    const keys = rows.map((r) => r.key);
    const hasMileageWarning = rows.some((r) => r.key === 'mileage' && r.mileageWarning != null);
    const sig = `${keys.join(',')}|${hasMileageWarning}`;
    if (rowsMetaSigRef.current === sig) return;
    rowsMetaSigRef.current = sig;
    onRowsMeta({ keys, hasMileageWarning });
  }, [rows, onRowsMeta]);

  // score_breakdown_opened now fires ONLY on a genuine user expand of the
  // collapsible header, never on mount. The section is defaultExpanded, so the
  // old mount-fire landed at the same millisecond as every listing_detail_viewed
  // and made the metric meaningless (one open == one breakdown "view"). Firing
  // exclusively on the toggle-to-expand turns it into a real engagement signal.
  // Consumed by trial-snapshot.ts to populate score_breakdowns_viewed for the
  // Reality Check / day-before email. model_id + marketcheck_id are required for
  // per-model attribution in the snapshot's HogQL group-by.
  const breakdownProps = useMemo(
    () => ({ model_id: modelId ?? null, marketcheck_id: marketcheckId ?? null }),
    [modelId, marketcheckId]
  );

  const handleToggle = useCallback(
    (sectionTitle, expanded) => {
      if (expanded && posthog) {
        posthog.capture('score_breakdown_opened', breakdownProps);
      }
      if (onToggle) onToggle(sectionTitle, expanded);
    },
    [posthog, onToggle, breakdownProps]
  );

  if (rows.length === 0) return null;

  const isDom = (key) => key === 'dom';

  const handleBlurredRowTap = (factorName) => {
    if (posthog) {
      posthog.capture('blurred_score_factor_unlock_tapped', { factor_name: factorName });
    }
    triggerUpgradeModal?.({ source: 'blurred_score_factor' });
  };

  const handleUnlockCtaTap = () => {
    if (posthog) {
      posthog.capture('blurred_score_factor_unlock_tapped', { factor_name: 'cta_button' });
    }
    triggerUpgradeModal?.({ source: 'blurred_score_factor' });
  };

  const renderRowContents = (row) => {
    const shouldBlurValue = !effectivePro && !isDom(row.key);
    return (
      <>
        <View style={styles.textColumn}>
          <View style={styles.primaryRow}>
            {shouldBlurValue && (
              <ProInlineUnlock feature="score_breakdown" />
            )}
            <View style={shouldBlurValue ? blurredValueStyle : undefined}>
              <Text style={styles.primaryText}>{row.primary}</Text>
            </View>
            {row.mileageWarning != null && (
              <MileageWarningBadge motMiles={row.mileageWarning} />
            )}
            {row.rateAnomaly != null && (
              <RateAnomalyBadge anomaly={row.rateAnomaly} />
            )}
          </View>
          <Text style={styles.secondaryText}>
            {SIGNAL_FRIENDLY_LABELS[row.key] || row.secondary}
          </Text>
        </View>
        <SignalTagBadge tag={row.tag} />
      </>
    );
  };

  return (
    <CollapsibleSection title="Score Breakdown" onToggle={handleToggle} hideTopBorder={hideTopBorder} proGated>
      <View style={styles.rows}>
        {rows.map((row) => {
          const isFreeGated = !effectivePro && !isDom(row.key);
          // The first-session tour highlights + scrolls to one factor row at a time.
          const isHl = highlightRowKey != null && row.key === highlightRowKey;
          const rowRef = isHl ? highlightRowRef : undefined;
          const rowStyle = isHl && rowHighlightStyle ? [styles.row, rowHighlightStyle] : styles.row;
          if (isFreeGated) {
            return (
              <Pressable
                key={row.key}
                ref={rowRef}
                style={rowStyle}
                onPress={() => handleBlurredRowTap(row.key)}
                accessibilityRole="button"
                accessibilityLabel={`Unlock ${SIGNAL_FRIENDLY_LABELS[row.key] || row.key} with Pro`}
              >
                {renderRowContents(row)}
              </Pressable>
            );
          }
          return (
            <View key={row.key} ref={rowRef} style={rowStyle}>
              {renderRowContents(row)}
            </View>
          );
        })}
        {!effectivePro && (
          <Pressable
            onPress={handleUnlockCtaTap}
            style={styles.unlockCta}
            accessibilityRole="button"
            accessibilityLabel="Unlock all score factors with Pro"
          >
            <View style={styles.unlockCtaBadge}>
              <Text style={styles.unlockCtaBadgeText}>Pro</Text>
            </View>
            <Text style={styles.unlockCtaText}>{trialUnlockEligible ? 'Unlock' : 'Unlock with Pro'}</Text>
          </Pressable>
        )}
      </View>
    </CollapsibleSection>
  );
};

// Web-only: CSS filter not supported by StyleSheet.create.
// `pointerEvents` intentionally omitted so the row-level Pressable receives taps.
const blurredValueStyle = {
  filter: 'blur(6px)',
  userSelect: 'none',
  opacity: 0.7,
};

const styles = StyleSheet.create({
  rows: {
    gap: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textColumn: {
    flex: 1,
    marginRight: 12,
    gap: 2,
  },
  primaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  primaryText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
  },
  secondaryText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    color: REDESIGN_COLORS.textSecondary,
  },
  unlockCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
  },
  unlockCtaBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: '#3b82f6',
    borderRadius: 4,
  },
  unlockCtaBadgeText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  unlockCtaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#3b82f6',
  },
});

export default ScoreBreakdownSection;
